import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWABAStatus } from '../store/slices/wabaSlice';
import { toast } from 'react-toastify';
import { apiService } from '../services/api';
import {
  buildEmbeddedSignupSetup,
  extractEmbeddedSignupSessionInfoFromMessage,
  normalizeEmbeddedSignupSessionInfo,
} from '../utils/embeddedSignupSetup';
import './WABASetup.css';

const FB_APP_ID = '1569886665141190';
const FB_CONFIG_ID = '1503207190859355';
const FB_SDK_VERSION = 'v25.0';

const WABASetup = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading: wabaLoading, hasWABA } = useSelector(state => state.waba);
  const user = useSelector((state) => state.auth?.user);
  const [isConnecting, setIsConnecting] = useState(false);
  const [autoChecked, setAutoChecked] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [embeddedSessionInfo, setEmbeddedSessionInfo] = useState(null);
  const [sessionInfoChecked, setSessionInfoChecked] = useState(false);
  const embeddedSessionInfoRef = useRef(null);

  // Load the Facebook JS SDK asynchronously
  useEffect(() => {
    const handleEmbeddedSignupMessage = (event) => {
      const normalizedPayload = extractEmbeddedSignupSessionInfoFromMessage(event);
      if (!normalizedPayload) return;

      console.log('[WABASetup] Received embedded signup session info from message event:', event.data);
      embeddedSessionInfoRef.current = normalizedPayload;
      setEmbeddedSessionInfo(normalizedPayload);
    };

    // Expose the session-info callback before the SDK loads
    window.fbEmbeddedSignupSessionInfo = (data) => {
      console.log('[WABASetup] Received embedded signup session info:', data);
      const sessionInfo = normalizeEmbeddedSignupSessionInfo(data);
      console.log('[WABASetup] Embedded signup session info:', sessionInfo);
      embeddedSessionInfoRef.current = sessionInfo;
      setEmbeddedSessionInfo(sessionInfo);
    };

    window.addEventListener('message', handleEmbeddedSignupMessage);

    if (document.getElementById('facebook-jssdk')) {
      if (window.FB) setSdkReady(true);
      return;
    }

    window.fbAsyncInit = function () {
      window.FB.init({
        appId: FB_APP_ID,
        autoLogAppEvents: true,
        xfbml: true,
        version: FB_SDK_VERSION,
      });
      setSdkReady(true);
    };

    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      window.removeEventListener('message', handleEmbeddedSignupMessage);
      delete window.fbEmbeddedSignupSessionInfo;
    };
  }, []);

  // Auto-check WABA status on mount
  useEffect(() => {
    if (!autoChecked && !wabaLoading) {
      console.log('[WABASetup] Auto-checking WABA status on mount...');
      dispatch(fetchWABAStatus())
        .unwrap()
        .then((result) => {
          console.log('[WABASetup] Auto-check result:', result);
          setAutoChecked(true);

          if (result && result.hasWABA) {
            console.log('[WABASetup] WABA found on mount, redirecting to dashboard');
            toast.success('WABA found! Redirecting to dashboard...');
            setTimeout(() => navigate('/dashboard'), 1000);
          }
        })
        .catch((err) => {
          console.error('[WABASetup] Auto-check error:', err);
          setAutoChecked(true);
        });
    }
  }, [autoChecked, wabaLoading, dispatch, navigate]);

  useEffect(() => {
    const sessionData = embeddedSessionInfo?.data;
    if (
      embeddedSessionInfo &&
      !sessionInfoChecked &&
      embeddedSessionInfo.event === 'FINISH' &&
      sessionData?.waba_id &&
      sessionData?.phone_number_id
    ) {
      console.log('[WABASetup] Embedded signup finished. Verifying WABA status...', sessionData);
      setSessionInfoChecked(true);
      setIsConnecting(true);

      dispatch(fetchWABAStatus())
        .unwrap()
        .then((result) => {
          if (result?.hasWABA) {
            toast.success('WhatsApp Business Account connected! Redirecting...');
            setTimeout(() => navigate('/dashboard'), 1200);
          } else {
            toast.info('Embedded signup finished. WABA not yet activated. Please check back shortly.');
          }
        })
        .catch((err) => {
          console.error('[WABASetup] Session info verify error:', err);
          toast.error('Could not verify connection status after signup. Please refresh or verify manually.');
        })
        .finally(() => {
          setIsConnecting(false);
        });
    }
  }, [embeddedSessionInfo, sessionInfoChecked, dispatch, navigate]);

  // Exchange the auth code returned by Embedded Signup with our backend
  const exchangeCodeWithBackend = useCallback(async (code, sessionInfo) => {
    try {
      const response = await apiService.waba.exchangeCode({ code, sessionInfo });
      return response.data;
    } catch (err) {
      throw new Error(err?.response?.data?.message || err.message || 'Code exchange failed');
    }
  }, []);

  // Launch the Facebook Embedded Signup popup
  const handleEmbeddedSignup = useCallback(() => {
    if (!sdkReady || !window.FB) {
      toast.error('Facebook SDK is still loading. Please try again in a moment.');
      return;
    }

    setIsConnecting(true);

    const setupPayload = buildEmbeddedSignupSetup(user || {});
    const loginExtras = {
      setup: setupPayload,
      featureType: '',
      sessionInfoVersion: '3',
    };

    window.FB.login(
      (response) => {
        console.log('[WABASetup] FB.login response:', response);

        if (response.authResponse?.code) {
          (async () => {
            try {
              const data = await exchangeCodeWithBackend(
                response.authResponse.code,
                embeddedSessionInfoRef.current || embeddedSessionInfo
              );
              console.log('[WABASetup] Code exchange success:', data);
              toast.success('WhatsApp Business Account connected! Redirecting...');
              setTimeout(() => navigate('/dashboard'), 1200);
            } catch (err) {
              console.error('[WABASetup] Code exchange error:', err);
              toast.error(err.message || 'Failed to connect your WhatsApp Business Account.');
            } finally {
              setIsConnecting(false);
            }
          })();
        } else if (response.status === 'connected') {
          // Fallback: status-based flow — re-check WABA on backend
          (async () => {
            try {
              const result = await dispatch(fetchWABAStatus()).unwrap();
              if (result?.hasWABA) {
                toast.success('WhatsApp Business Account connected! Redirecting...');
                setTimeout(() => navigate('/dashboard'), 1200);
              } else {
                toast.warning('Signup completed but WABA not yet active. Please check back shortly.');
              }
            } catch {
              toast.error('Could not verify connection status. Please use "Already Connected? Verify Now".');
            } finally {
              setIsConnecting(false);
            }
          })();
        } else {
          // User cancelled or closed the popup
          console.log('[WABASetup] Embedded signup cancelled or failed:', response.status);
          toast.info('WhatsApp Business Account setup was not completed.');
          setIsConnecting(false);
        }
      },
      {
        config_id: FB_CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras: loginExtras,
      }
    );
  }, [sdkReady, exchangeCodeWithBackend, dispatch, navigate, user]);

  const handleRefreshStatus = async () => {
    try {
      console.log('[WABASetup] Checking WABA status...');
      setIsConnecting(true);
      const result = await dispatch(fetchWABAStatus()).unwrap();
      console.log('[WABASetup] WABA status result:', result);
      
      if (result && result.hasWABA) {
        toast.success('WABA found! Redirecting to dashboard...');
        // Give a short delay then redirect to dashboard
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);
      } else {
        toast.error('No WABA found. Please complete the connection process first.');
      }
    } catch (error) {
      console.error('[WABASetup] Error checking WABA status:', error);
      toast.error(error?.message || 'Failed to check WABA status. Please try again.');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleContactSupport = () => {
    // Open support contact form or link
    window.open('mailto:support@hawktrion.com?subject=WABA%20Setup%20Assistance', '_blank');
  };

  return (
    <div className="waba-setup-container">
      <div className="waba-setup-content">
        {/* Header Section */}
        <div className="waba-setup-header">
          <div className="waba-icon">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="#25D366">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
          </div>
          <h1>WhatsApp Business Account Setup Required</h1>
          <p className="waba-subtitle">
            To access all features and start messaging with your customers, you need to set up your WhatsApp Business Account (WABA).
          </p>
        </div>

        {/* Steps Section */}
        <div className="waba-setup-steps">
          <div className="steps-title">How to Set Up Your WABA</div>
          <div className="steps-container">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Verify Your Identity</h3>
                <p>Provide your business details and personal information to verify your identity with WhatsApp.</p>
              </div>
            </div>

            <div className="step-arrow">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </div>

            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Connect Your Phone Number</h3>
                <p>Link a valid phone number to your business account. You'll receive a verification code via SMS.</p>
              </div>
            </div>

            <div className="step-arrow">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </div>

            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Authorize Access</h3>
                <p>Grant Hawktrion permission to manage your WhatsApp Business Account messages and features.</p>
              </div>
            </div>

            <div className="step-arrow">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </div>

            <div className="step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Complete Setup</h3>
                <p>Configure your business profile and start managing conversations from Hawktrion dashboard.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="waba-features">
          <h3>What You Can Do After Setup</h3>
          <div className="features-grid">
            <div className="feature">
              <div className="feature-icon">💬</div>
              <div className="feature-text">
                <h4>Send & Receive Messages</h4>
                <p>Communicate with your customers in real-time</p>
              </div>
            </div>
            <div className="feature">
              <div className="feature-icon">📝</div>
              <div className="feature-text">
                <h4>Message Templates</h4>
                <p>Create and manage pre-approved message templates</p>
              </div>
            </div>
            <div className="feature">
              <div className="feature-icon">📢</div>
              <div className="feature-text">
                <h4>Broadcast Campaigns</h4>
                <p>Send campaigns to multiple customers at once</p>
              </div>
            </div>
            <div className="feature">
              <div className="feature-icon">👥</div>
              <div className="feature-text">
                <h4>Customer Management</h4>
                <p>Organize and manage your customer relationships</p>
              </div>
            </div>
            <div className="feature">
              <div className="feature-icon">📊</div>
              <div className="feature-text">
                <h4>Analytics & Reports</h4>
                <p>Track message delivery and customer engagement</p>
              </div>
            </div>
            <div className="feature">
              <div className="feature-icon">⚙️</div>
              <div className="feature-text">
                <h4>Advanced Settings</h4>
                <p>Customize your business profile and preferences</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="waba-setup-cta">
          <button
            className="btn btn-primary btn-large"
            onClick={handleEmbeddedSignup}
            disabled={isConnecting || !sdkReady}
          >
            <span className="btn-text">
              {isConnecting
                ? 'Connecting...'
                : !sdkReady
                ? 'Loading...'
                : 'Connect WhatsApp Business Account'}
            </span>
            <span className="btn-icon">→</span>
          </button>
          
          <div className="cta-divider">or</div>

          <button 
            className="btn btn-secondary btn-large"
            onClick={handleRefreshStatus}
            disabled={isConnecting || wabaLoading}
          >
            <span className="btn-text">
              {isConnecting || wabaLoading ? 'Checking...' : 'Already Connected? Verify Now'}
            </span>
            <span className="btn-icon">↻</span>
          </button>

          <div className="cta-divider">or</div>
          
          <button 
            className="btn btn-secondary btn-large"
            onClick={handleContactSupport}
            disabled={isConnecting}
          >
            <span className="btn-text">Need Help? Contact Support</span>
            <span className="btn-icon">→</span>
          </button>
        </div>

        {/* Info Box */}
        <div className="waba-info-box">
          <div className="info-icon">ℹ️</div>
          <div className="info-text">
            <strong>Setting up WABA is quick and secure.</strong> It typically takes 5-10 minutes to complete. 
            We never store your WhatsApp Business Account credentials directly. All communication is encrypted 
            and secure.
          </div>
        </div>
      </div>
    </div>
  );
};

export default WABASetup;
